import React, { useState, useEffect, useRef } from 'react';
import {
  Bot, Send, Paperclip, Download, Cloud, Trash2, Plus, RefreshCw,
  FileText, FileSpreadsheet, FileCode, Image as ImageIcon, FileArchive,
  File, ArrowLeft, CheckCircle2, Copy, Sparkles, Cpu, Folder, X,
  Check, AlertCircle, HardDrive, CornerDownLeft, Maximize2, Minimize2,
  Lock, Unlock, KeyRound, ShieldAlert, Eye, EyeOff, Radar
} from 'lucide-react';

interface AssistantFile {
  id: string;
  originalName: string;
  filename: string;
  size: number;
  mimeType?: string;
  type?: string;
  isImage?: boolean;
  downloadUrl?: string;
  gdriveSynced?: boolean;
  gdrivePath?: string;
}

interface AssistantMessage {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  files?: AssistantFile[];
  created_at?: string;
}

interface AssistantSession {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

interface AdminAssistantConsoleProps {
  onBack: () => void;
  language: 'uk' | 'en' | 'pl';
  currentUserEmail?: string;
  onOpenTrembitaMesh?: () => void;
}

export default function AdminAssistantConsole({
  onBack,
  language,
  currentUserEmail,
  onOpenTrembitaMesh
}: AdminAssistantConsoleProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';

  // Security Lock State
  const [isUnlocked, setIsUnlocked] = useState<boolean>(() => !!sessionStorage.getItem('antigravity_assistant_pin'));
  const [pinInput, setPinInput] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);
  const [isVerifyingPin, setIsVerifyingPin] = useState(false);

  const [sessions, setSessions] = useState<AssistantSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [stagedFiles, setStagedFiles] = useState<AssistantFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [selectedModel, setSelectedModel] = useState<'agy-core' | 'gemini-4-argon' | 'gemini-3.7-flash' | 'gemini-2.5-flash'>('agy-core');
  const [gdriveSyncingId, setGdriveSyncingId] = useState<string | null>(null);
  const [gdriveStatusToast, setGdriveStatusToast] = useState<{ id: string; message: string; isError?: boolean } | null>(null);
  const [copiedCodeIndex, setCopiedCodeIndex] = useState<string | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [gdriveTargetFolder, setGdriveTargetFolder] = useState('gdrive:DeepResearchHub_Admin/');
  const [showFolderModal, setShowFolderModal] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const getStoredPin = () => sessionStorage.getItem('antigravity_assistant_pin') || '';

  const getAuthHeaders = (extra: Record<string, string> = {}) => {
    const pin = getStoredPin();
    return {
      'x-assistant-pin': pin,
      ...extra
    };
  };

  // Verify PIN with backend
  const handleVerifyPin = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!pinInput.trim() || isVerifyingPin) return;

    setIsVerifyingPin(true);
    setPinError(null);

    try {
      const res = await fetch('/api/admin/assistant/verify-pin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: pinInput.trim() })
      });

      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        sessionStorage.setItem('antigravity_assistant_pin', pinInput.trim());
        setIsUnlocked(true);
        setPinError(null);
      } else {
        setPinError(
          isPl 
            ? 'Nieprawidłowe hasło dostępu. Spróbuj ponownie.' 
            : isUk 
            ? 'Невірний пароль доступу. Спробуйте ще раз.' 
            : 'Invalid security password. Try again.'
        );
      }
    } catch (err: any) {
      setPinError(
        isPl ? 'Błąd połączenia z serwerem' : isUk ? 'Помилка з\'єднання з сервером' : 'Connection error'
      );
    } finally {
      setIsVerifyingPin(false);
    }
  };

  // Lock Console
  const handleLockConsole = () => {
    sessionStorage.removeItem('antigravity_assistant_pin');
    setIsUnlocked(false);
    setPinInput('');
    setPinError(null);
  };

  // Auto-scroll to bottom of chat
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isSending]);

  // Load Sessions
  const loadSessions = async () => {
    try {
      const res = await fetch('/api/admin/assistant/sessions', {
        headers: getAuthHeaders()
      });
      if (res.status === 403) {
        setIsUnlocked(false);
        return;
      }
      if (res.ok) {
        const data = await res.json();
        setSessions(data);
        if (data.length > 0 && !activeSessionId) {
          setActiveSessionId(data[0].id);
        } else if (data.length === 0) {
          handleCreateNewSession();
        }
      }
    } catch (err) {
      console.error('Error loading assistant sessions:', err);
    }
  };

  useEffect(() => {
    if (isUnlocked) {
      loadSessions();
    }
  }, [isUnlocked]);

  // Load Messages for active session
  useEffect(() => {
    if (!activeSessionId || !isUnlocked) return;
    const loadMessages = async () => {
      try {
        const res = await fetch(`/api/admin/assistant/sessions/${activeSessionId}/messages`, {
          headers: getAuthHeaders()
        });
        if (res.status === 403) {
          setIsUnlocked(false);
          return;
        }
        if (res.ok) {
          const data = await res.json();
          setMessages(data);
        }
      } catch (err) {
        console.error('Error loading messages:', err);
      }
    };
    loadMessages();
  }, [activeSessionId, isUnlocked]);

  // Create new session
  const handleCreateNewSession = async () => {
    try {
      const title = isPl 
        ? `Zadanie ${new Date().toLocaleDateString('pl-PL')} ${new Date().toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}`
        : isUk 
        ? `Завдання ${new Date().toLocaleDateString('uk-UA')} ${new Date().toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' })}`
        : `Task ${new Date().toLocaleDateString('en-US')} ${new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;

      const res = await fetch('/api/admin/assistant/sessions', {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ title })
      });
      if (res.status === 403) {
        setIsUnlocked(false);
        return;
      }
      if (res.ok) {
        const newSession = await res.json();
        setSessions(prev => [newSession, ...prev]);
        setActiveSessionId(newSession.id);
        setMessages([]);
      }
    } catch (err) {
      console.error('Error creating new session:', err);
    }
  };

  // Delete session
  const handleDeleteSession = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm(isPl ? 'Usunąć ten dialog?' : isUk ? 'Видалити цей діалог?' : 'Delete this conversation?')) return;
    try {
      const res = await fetch(`/api/admin/assistant/sessions/${id}`, { 
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (res.status === 403) {
        setIsUnlocked(false);
        return;
      }
      if (res.ok) {
        const filtered = sessions.filter(s => s.id !== id);
        setSessions(filtered);
        if (activeSessionId === id) {
          if (filtered.length > 0) setActiveSessionId(filtered[0].id);
          else handleCreateNewSession();
        }
      }
    } catch (err) {
      console.error('Error deleting session:', err);
    }
  };

  // Handle File Upload
  const handleFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    try {
      const res = await fetch('/api/admin/assistant/upload', {
        method: 'POST',
        headers: { 'x-assistant-pin': getStoredPin() },
        body: formData
      });
      if (res.status === 403) {
        setIsUnlocked(false);
        return;
      }
      if (res.ok) {
        const data = await res.json();
        setStagedFiles(prev => [...prev, ...data.files]);
      } else {
        alert(isPl ? 'Błąd podczas wgrywania pliku' : isUk ? 'Помилка завантаження файлу' : 'File upload failed');
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      alert(isPl ? 'Błąd sieci podczas wgrywania' : isUk ? 'Мережева помилка завантаження' : 'Network error uploading file');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Remove staged file
  const handleRemoveStagedFile = (id: string) => {
    setStagedFiles(prev => prev.filter(f => f.id !== id));
  };

  // Send Message
  const handleSendMessage = async () => {
    if ((!inputText.trim() && stagedFiles.length === 0) || isSending) return;
    if (!activeSessionId) return;

    const currentText = inputText.trim();
    const currentFiles = [...stagedFiles];

    setInputText('');
    setStagedFiles([]);
    setIsSending(true);

    // Optimistically add user message
    const optimisticUserMsg: AssistantMessage = {
      role: 'user',
      content: currentText,
      files: currentFiles,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, optimisticUserMsg]);

    try {
      const res = await fetch('/api/admin/assistant/chat', {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          sessionId: activeSessionId,
          message: currentText,
          files: currentFiles,
          model: selectedModel,
          language
        })
      });

      if (res.status === 403) {
        const errData = await res.json().catch(() => ({}));
        if (errData.requiresPin) {
          setIsUnlocked(false);
          return;
        }
      }

      if (res.ok) {
        const assistantReply = await res.json();
        setMessages(prev => [...prev, assistantReply]);
        // Update session list order
        setSessions(prev => {
          const current = prev.find(s => s.id === activeSessionId);
          if (!current) return prev;
          const rest = prev.filter(s => s.id !== activeSessionId);
          return [{ ...current, updated_at: new Date().toISOString() }, ...rest];
        });
      } else {
        const errData = await res.json().catch(() => ({}));
        setMessages(prev => [
          ...prev,
          {
            role: 'assistant',
            content: `⚠️ ${isPl ? 'Wystąpił błąd podczas generowania odpowiedzi' : isUk ? 'Сталася помилка при отриманні відповіді від асистента' : 'Error generating response'}: ${errData.error || 'Server error'}`,
            created_at: new Date().toISOString()
          }
        ]);
      }
    } catch (err: any) {
      console.error('Error in chat request:', err);
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: `⚠️ ${isPl ? 'Błąd połączenia' : isUk ? 'Помилка з\'єднання' : 'Connection error'}: ${err.message}`,
          created_at: new Date().toISOString()
        }
      ]);
    } finally {
      setIsSending(false);
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  };

  // Save to Google Drive
  const handleSaveToGoogleDrive = async (file: AssistantFile, folderOverride?: string) => {
    setGdriveSyncingId(file.id);
    const targetFolder = folderOverride || gdriveTargetFolder || 'gdrive:DeepResearchHub_Admin/';
    setShowFolderModal(null);

    try {
      const res = await fetch('/api/admin/assistant/save-to-gdrive', {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          fileId: file.id,
          folder: targetFolder
        })
      });

      if (res.status === 403) {
        setIsUnlocked(false);
        return;
      }

      const data = await res.json();
      if (res.ok && data.success) {
        setGdriveStatusToast({
          id: file.id,
          message: `${isPl ? 'Zapisano na Google Drive' : isUk ? 'Успішно збережено на Google Drive' : 'Saved to Google Drive'}: ${data.destination}`
        });

        // Update local message file state
        setMessages(prev => prev.map(m => {
          if (!m.files) return m;
          return {
            ...m,
            files: m.files.map(f => f.id === file.id ? { ...f, gdriveSynced: true, gdrivePath: data.destination } : f)
          };
        }));
      } else {
        setGdriveStatusToast({
          id: file.id,
          message: `❌ ${data.error || 'Помилка синхронізації з Google Drive'}`,
          isError: true
        });
      }
    } catch (err: any) {
      setGdriveStatusToast({
        id: file.id,
        message: `❌ ${err.message}`,
        isError: true
      });
    } finally {
      setGdriveSyncingId(null);
      setTimeout(() => setGdriveStatusToast(null), 5000);
    }
  };

  // Helper for File Type Icons
  const renderFileIcon = (file: AssistantFile) => {
    const ext = file.originalName.split('.').pop()?.toLowerCase() || '';
    if (['doc', 'docx'].includes(ext)) {
      return <FileText className="w-5 h-5 text-blue-400 shrink-0" />;
    }
    if (['xls', 'xlsx', 'csv'].includes(ext)) {
      return <FileSpreadsheet className="w-5 h-5 text-emerald-400 shrink-0" />;
    }
    if (['py', 'js', 'ts', 'sh', 'json', 'sql', 'html', 'css'].includes(ext)) {
      return <FileCode className="w-5 h-5 text-amber-400 shrink-0" />;
    }
    if (['png', 'jpg', 'jpeg', 'webp', 'gif', 'svg'].includes(ext)) {
      return <ImageIcon className="w-5 h-5 text-purple-400 shrink-0" />;
    }
    if (['zip', 'tar', 'gz', '7z', 'rar'].includes(ext)) {
      return <FileArchive className="w-5 h-5 text-orange-400 shrink-0" />;
    }
    return <File className="w-5 h-5 text-slate-400 shrink-0" />;
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  // Copy code block to clipboard
  const handleCopyCode = (codeText: string, indexId: string) => {
    navigator.clipboard.writeText(codeText);
    setCopiedCodeIndex(indexId);
    setTimeout(() => setCopiedCodeIndex(null), 2000);
  };

  // Preset Prompts for instant execution
  const presetPrompts = [
    {
      title: isPl ? '📄 Raport DSTU (.docx)' : isUk ? '📄 Звіт за ДСТУ (.docx)' : '📄 DSTU Report (.docx)',
      prompt: isPl 
        ? 'Utwórz profesjonalny oficjalny raport zgodnie z normą DSTU 4163:2020 i wygeneruj gotowy do pobrania plik Word .docx.'
        : isUk
        ? 'Склади офіційний структурований звіт за ДСТУ 4163:2020 з розділами, аналітичними висновками та згенеруй готовий до завантаження файл Word .docx.'
        : 'Generate an official structured DSTU report with sections and output a ready-to-download Word .docx document.'
    },
    {
      title: isPl ? '📊 Tabela Excel (.xlsx)' : isUk ? '📊 Таблиця Excel (.xlsx)' : '📊 Excel Table (.xlsx)',
      prompt: isPl
        ? 'Utwórz tabelę kalkulacyjną Excel z żywymi formułami (SUM, SUMIF, VAT 20%) i wygeneruj plik .xlsx.'
        : isUk
        ? 'Склади розрахункову кошторисну таблицю з формулами SUM, ПДВ 20% та підсумками, і згенеруй повноцінний файл Excel .xlsx.'
        : 'Create a financial spreadsheet with formulas (SUM, VAT 20%) and output a downloadable Excel .xlsx file.'
    },
    {
      title: isPl ? '🐍 Skrypt Python (.py)' : isUk ? '🐍 Скрипт Python (.py)' : '🐍 Python Script (.py)',
      prompt: isPl
        ? 'Napisz wydajny skrypt w Pythonie do automatyzacji zadania i wygeneruj plik .py do pobrania.'
        : isUk
        ? 'Напиши надійний, оптимізований Python скрипт для обробки та автоматизації, і надай його окремим файлом .py.'
        : 'Write a high-performance Python script for processing and output a ready-to-use .py file.'
    },
    {
      title: isPl ? '🔍 Badanie i Analiza' : isUk ? '🔍 Глибоке Дослідження' : '🔍 Deep Research',
      prompt: isPl
        ? 'Przeprowadź dogłębną analizę techniczną i prawną z uwzględnieniem najnowszych standardów.'
        : isUk
        ? 'Проведи глибокий аналіз актуального стану справ, технічних регламентів та надай структуровані рекомендації.'
        : 'Conduct a thorough deep-research analysis with references and clear conclusions.'
    }
  ];

  // If not unlocked with security PIN, render dedicated security lock screen
  if (!isUnlocked) {
    return (
      <div className="relative flex flex-col h-[calc(100vh-5rem)] bg-slate-950 text-slate-100 font-sans rounded-2xl border border-slate-800 shadow-2xl overflow-hidden items-center justify-center p-4 select-none">
        {/* Ambient lighting */}
        <div className="absolute -top-32 -left-32 w-80 h-80 bg-cyan-600/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -right-32 w-80 h-80 bg-emerald-600/10 rounded-full blur-3xl pointer-events-none" />

        {/* Top Back bar */}
        <div className="absolute top-4 left-4 z-10">
          <button
            onClick={onBack}
            className="px-3.5 py-1.5 bg-slate-900/90 hover:bg-slate-800 text-slate-300 hover:text-white rounded-xl border border-slate-700 flex items-center gap-2 text-xs font-semibold transition-all shadow-md cursor-pointer backdrop-blur-sm"
          >
            <ArrowLeft className="w-4 h-4 text-emerald-400" />
            <span>{isPl ? 'Powrót do Centrum' : isUk ? 'Повернутися назад' : 'Back to Hub'}</span>
          </button>
        </div>

        {/* Lock Card */}
        <div className="max-w-md w-full bg-slate-900/95 border border-slate-700/80 rounded-2xl shadow-2xl p-6 sm:p-8 backdrop-blur-md relative z-10 animate-fade-in">
          <div className="flex flex-col items-center text-center">
            {/* Glowing Shield Icon */}
            <div className="relative mb-5">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-cyan-950 to-slate-900 border border-cyan-500/40 shadow-xl shadow-cyan-950/60 flex items-center justify-center">
                <ShieldAlert className="w-9 h-9 sm:w-10 sm:h-10 text-cyan-400 animate-pulse" />
              </div>
              <div className="absolute -bottom-1 -right-1 bg-emerald-600 text-white p-1.5 rounded-lg border border-slate-900 shadow-md">
                <KeyRound className="w-4 h-4" />
              </div>
            </div>

            {/* Badges */}
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-cyan-950/60 border border-cyan-500/30 text-cyan-300 text-[11px] font-bold uppercase tracking-wider mb-3">
              <Lock className="w-3 h-3 text-cyan-400" />
              <span>{isPl ? 'Dostęp Chroniony Hasłem' : isUk ? 'Захищений Доступ Адміністратора' : 'Restricted Admin Access'}</span>
            </div>

            <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight mb-2">
              Antigravity Core Console
            </h2>
            <p className="text-xs sm:text-sm text-slate-400 leading-relaxed mb-6">
              {isPl
                ? 'Wprowadź dodatkowe hasło bezpieczeństwa, aby uzyskać dostęp do terminalnego agenta Antigravity na serwerze.'
                : isUk
                ? 'Введіть додатковий пароль безпеки для доступу до веб-інтерфейсу термінального агента Antigravity на сервері.'
                : 'Enter additional security password to access direct Antigravity terminal agent on the server.'}
            </p>

            {/* Form */}
            <form onSubmit={handleVerifyPin} className="w-full space-y-4">
              <div className="relative">
                <input
                  type={showPin ? 'text' : 'password'}
                  value={pinInput}
                  onChange={(e) => {
                    setPinInput(e.target.value);
                    if (pinError) setPinError(null);
                  }}
                  placeholder={isPl ? 'Wprowadź hasło dostępu...' : isUk ? 'Введіть пароль доступу...' : 'Enter security password...'}
                  autoFocus
                  disabled={isVerifyingPin}
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3 text-white text-sm font-mono tracking-widest placeholder:text-slate-500 placeholder:font-sans placeholder:tracking-normal focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-500/20 pr-11 transition-all shadow-inner"
                />
                <button
                  type="button"
                  onClick={() => setShowPin(!showPin)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 transition-colors cursor-pointer"
                  title={showPin ? 'Сховати' : 'Показати'}
                >
                  {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              {pinError && (
                <div className="flex items-center gap-2 p-3 bg-rose-950/60 border border-rose-800 rounded-xl text-rose-300 text-xs text-left">
                  <AlertCircle className="w-4 h-4 shrink-0 text-rose-400" />
                  <span>{pinError}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={isVerifyingPin || !pinInput.trim()}
                className="w-full py-3 px-4 bg-gradient-to-r from-cyan-600 via-teal-600 to-emerald-600 hover:from-cyan-500 hover:via-teal-500 hover:to-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-bold rounded-xl shadow-lg shadow-cyan-950/50 flex items-center justify-center gap-2 transition-all cursor-pointer"
              >
                {isVerifyingPin ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>{isPl ? 'Weryfikacja...' : isUk ? 'Перевірка...' : 'Verifying...'}</span>
                  </>
                ) : (
                  <>
                    <KeyRound className="w-4 h-4 text-cyan-200" />
                    <span>{isPl ? 'Odblokuj Dostęp' : isUk ? 'Розблокувати Доступ' : 'Unlock Access'}</span>
                  </>
                )}
              </button>
            </form>

            {/* Note */}
            <div className="mt-6 pt-4 border-t border-slate-800 text-[11px] text-slate-500 text-center flex items-center justify-center gap-1.5">
              <Lock className="w-3 h-3 text-slate-500" />
              <span>
                {isPl
                  ? 'Wymagana podwójna autoryzacja administratora.'
                  : isUk
                  ? 'Потрібна додаткова авторизація адміністратора.'
                  : 'Double administrator authorization required.'}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100dvh-5rem)] min-h-[500px] bg-slate-950 text-slate-100 font-mono rounded-xl sm:rounded-2xl border border-slate-800 shadow-2xl overflow-hidden animate-fade-in">
      {/* TOP HEADER */}
      <div className="flex items-center justify-between px-2.5 sm:px-4 py-2 sm:py-3 bg-slate-900 border-b border-slate-800 shrink-0 gap-2">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <button
            onClick={onBack}
            className="px-2.5 sm:px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-amber-400 hover:text-amber-300 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 border border-amber-500/30 cursor-pointer shrink-0"
            title={isPl ? 'Powrót do Centrum Administracyjnego' : isUk ? 'Назад до Адмін-Центру' : 'Back to Admin Hub'}
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{isPl ? 'Centrum Admina' : isUk ? 'Адмін-Центр' : 'Admin Hub'}</span>
          </button>

          <div className="h-4 w-px bg-slate-700 hidden sm:block shrink-0" />

          <div className="flex items-center gap-2 min-w-0">
            <div className="relative shrink-0">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-tr from-emerald-600 to-teal-400 flex items-center justify-center text-white shadow-lg shadow-emerald-500/20">
                <Bot className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <span className="absolute -bottom-0.5 -right-0.5 w-2 sm:w-2.5 h-2 sm:h-2.5 bg-emerald-400 border-2 border-slate-900 rounded-full animate-pulse" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <h1 className="text-xs sm:text-sm font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-300 truncate">
                  Antigravity Core
                </h1>
                <span className="px-1.5 py-0.5 bg-emerald-500/20 text-emerald-300 text-[9px] sm:text-[10px] font-bold rounded border border-emerald-500/30 shrink-0">
                  ADMIN
                </span>
              </div>
              <p className="text-[10px] text-slate-400 hidden sm:block truncate">
                {isPl 
                  ? 'Bezpośredni terminal asystenta: zadania, wymiana plików i zapis na Google Drive' 
                  : isUk 
                  ? 'Прямий термінал асистента: завдання, обмін файлами та запис на Google Drive' 
                  : 'Direct assistant console: tasks, file exchange and Google Drive sync'}
              </p>
            </div>
          </div>
        </div>

        {/* Model & Drive Controls */}
        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          {/* Mobile History Drawer Toggle */}
          <button
            onClick={() => setIsMobileDrawerOpen(true)}
            className="lg:hidden px-2 py-1.5 bg-slate-800 hover:bg-slate-700 text-emerald-400 hover:text-emerald-300 rounded-lg border border-slate-700 text-xs font-bold transition-all cursor-pointer flex items-center gap-1 shadow-sm"
            title={isPl ? 'Historia zadań' : isUk ? 'Історія завдань' : 'Task History'}
          >
            <Folder className="w-3.5 h-3.5" />
            <span className="text-[10px] bg-emerald-500/20 px-1 py-0.2 rounded font-bold">{sessions.length}</span>
          </button>

          {/* Trembita-MESH Shortcut Button */}
          {onOpenTrembitaMesh && (
            <button
              onClick={onOpenTrembitaMesh}
              className="px-2 sm:px-2.5 py-1 bg-red-950/80 hover:bg-red-900 text-red-300 border border-red-500/40 rounded-lg text-[11px] font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
              title="Перейти до тактичного комплексу «Трембіта-МЕШ»"
            >
              <Radar className="w-3.5 h-3.5 text-red-400" />
              <span className="hidden sm:inline">Трембіта-МЕШ</span>
            </button>
          )}

          {/* Google Drive Status Pill (Desktop only) */}
          <div 
            className="hidden md:flex items-center gap-1.5 px-2.5 py-1 bg-amber-500/10 border border-amber-500/30 rounded-lg text-[11px] text-amber-300 cursor-pointer hover:bg-amber-500/20 transition-all shrink-0"
            onClick={() => setShowFolderModal('global')}
            title={isPl ? 'Folder docelowy Google Drive' : isUk ? 'Цільова папка на Google Drive' : 'Target Google Drive folder'}
          >
            <Cloud className="w-3.5 h-3.5 text-amber-400" />
            <span className="font-semibold max-w-[140px] truncate">{gdriveTargetFolder}</span>
          </div>

          {/* Model Selector */}
          <div className="flex items-center bg-slate-800 border border-slate-700 rounded-lg p-0.5 text-xs shrink-0">
            <button
              onClick={() => setSelectedModel('agy-core')}
              className={`px-1.5 sm:px-2.5 py-1 rounded text-[10px] font-bold transition-all cursor-pointer flex items-center gap-1 ${
                selectedModel === 'agy-core'
                  ? 'bg-gradient-to-r from-emerald-600 to-teal-500 text-white shadow-xs'
                  : 'text-slate-400 hover:text-white'
              }`}
              title="Повноцінний ШІ-Агент Antigravity CLI з доступом до сервера, пам'яті та інструментів"
            >
              <Cpu className="w-3 h-3" />
              <span>Core</span>
            </button>
            <button
              onClick={() => setSelectedModel('gemini-2.5-flash')}
              className={`px-1.5 sm:px-2 py-1 rounded text-[10px] font-bold transition-all cursor-pointer ${
                selectedModel === 'gemini-2.5-flash'
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'text-slate-400 hover:text-white'
              }`}
              title="Швидкісна модель Gemini 2.5 Flash"
            >
              Flash
            </button>
            <button
              onClick={() => setSelectedModel('gemini-3.7-flash')}
              className={`px-1.5 sm:px-2 py-1 rounded text-[10px] font-bold transition-all cursor-pointer ${
                selectedModel === 'gemini-3.7-flash'
                  ? 'bg-purple-600 text-white shadow-xs'
                  : 'text-slate-400 hover:text-white'
              }`}
              title="Флагманська модель Gemini 3.7 Flash"
            >
              3.7 Flash
            </button>
            <button
              onClick={() => setSelectedModel('gemini-4-argon')}
              className={`px-1.5 sm:px-2 py-1 rounded text-[10px] font-bold transition-all cursor-pointer flex items-center gap-1 ${
                selectedModel === 'gemini-4-argon'
                  ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-xs ring-1 ring-amber-300/50'
                  : 'text-amber-400/90 hover:text-amber-200 hover:bg-slate-700/60'
              }`}
              title="Флагманська reasoning-модель Gemini 4 Argon (1M Token Output, Embodied 3D, Gray Swan Resilience)"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"></span>
              <span>4 Argon</span>
            </button>
          </div>

          {/* Toggle Sidebar (Desktop only) */}
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="hidden lg:flex p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg border border-slate-700 text-xs transition-colors cursor-pointer"
            title={isSidebarOpen ? 'Сховати історію' : 'Показати історію'}
          >
            {isSidebarOpen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>

          {/* Lock Console Button */}
          <button
            onClick={handleLockConsole}
            className="p-1.5 bg-rose-950/40 hover:bg-rose-900/60 text-rose-300 hover:text-white rounded-lg border border-rose-800/60 text-xs transition-all cursor-pointer flex items-center gap-1 sm:px-2.5 shadow-sm shrink-0"
            title={isPl ? 'Zablokuj konsolę' : isUk ? 'Заблокувати консоль' : 'Lock console'}
          >
            <Lock className="w-3.5 h-3.5 text-rose-400" />
            <span className="hidden sm:inline font-semibold">{isPl ? 'Zablokuj' : isUk ? 'Заблокувати' : 'Lock'}</span>
          </button>
        </div>
      </div>

      {/* BODY SPLIT VIEW */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* DESKTOP SESSIONS SIDEBAR (Visible on lg+ screens only) */}
        {isSidebarOpen && (
          <div className="hidden lg:flex w-64 sm:w-72 bg-slate-900/90 border-r border-slate-800 flex-col shrink-0 animate-fade-in">
            <div className="p-3 border-b border-slate-800 flex items-center justify-between">
              <span className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                {isPl ? 'Historia zadań' : isUk ? 'Історія завдань' : 'Task History'}
              </span>
              <button
                onClick={handleCreateNewSession}
                className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded-md text-xs font-bold flex items-center gap-1 transition-all cursor-pointer shadow-sm"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>{isPl ? 'Nowy' : isUk ? 'Новий' : 'New'}</span>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-2 space-y-1 scrollbar-thin">
              {sessions.map(s => (
                <div
                  key={s.id}
                  onClick={() => setActiveSessionId(s.id)}
                  className={`p-2.5 rounded-xl text-xs transition-all cursor-pointer flex items-center justify-between group border ${
                    activeSessionId === s.id
                      ? 'bg-emerald-950/40 border-emerald-500/40 text-emerald-200 font-bold'
                      : 'border-transparent text-slate-300 hover:bg-slate-800/60 hover:text-white'
                  }`}
                >
                  <div className="truncate pr-2 flex-1">
                    <p className="truncate">{s.title}</p>
                    <span className="text-[10px] text-slate-500 font-normal">
                      {new Date(s.updated_at).toLocaleDateString()}
                    </span>
                  </div>
                  <button
                    onClick={(e) => handleDeleteSession(s.id, e)}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-500/20 hover:text-red-400 rounded text-slate-500 transition-all"
                    title={isPl ? 'Usuń' : isUk ? 'Видалити' : 'Delete'}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* MOBILE SESSIONS DRAWER (Overlay on screens < lg) */}
        {isMobileDrawerOpen && (
          <div className="fixed inset-0 z-50 lg:hidden flex animate-fade-in">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-slate-950/80 backdrop-blur-xs"
              onClick={() => setIsMobileDrawerOpen(false)}
            />
            {/* Drawer Content */}
            <div className="relative w-72 max-w-[80vw] bg-slate-900 border-r border-slate-800 flex flex-col z-50 shadow-2xl h-full">
              <div className="p-3 border-b border-slate-800 flex items-center justify-between">
                <span className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                  {isPl ? 'Historia zadań' : isUk ? 'Історія завдань' : 'Task History'}
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => {
                      handleCreateNewSession();
                      setIsMobileDrawerOpen(false);
                    }}
                    className="px-2 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded-md text-xs font-bold flex items-center gap-1 shadow-sm cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>{isPl ? 'Nowy' : isUk ? 'Новий' : 'New'}</span>
                  </button>
                  <button
                    onClick={() => setIsMobileDrawerOpen(false)}
                    className="p-1 text-slate-400 hover:text-white rounded-md hover:bg-slate-800 cursor-pointer"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-2 space-y-1 scrollbar-thin">
                {sessions.map(s => (
                  <div
                    key={s.id}
                    onClick={() => {
                      setActiveSessionId(s.id);
                      setIsMobileDrawerOpen(false);
                    }}
                    className={`p-2.5 rounded-xl text-xs transition-all cursor-pointer flex items-center justify-between border ${
                      activeSessionId === s.id
                        ? 'bg-emerald-950/40 border-emerald-500/40 text-emerald-200 font-bold'
                        : 'border-transparent text-slate-300 hover:bg-slate-800/60 hover:text-white'
                    }`}
                  >
                    <div className="truncate pr-2 flex-1">
                      <p className="truncate">{s.title}</p>
                      <span className="text-[10px] text-slate-500 font-normal">
                        {new Date(s.updated_at).toLocaleDateString()}
                      </span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSession(s.id, e);
                      }}
                      className="p-1 hover:bg-red-500/20 hover:text-red-400 rounded text-slate-500 transition-all cursor-pointer"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* MAIN CHAT & ARTIFACT AREA */}
        <div className="flex-1 flex flex-col bg-slate-950 overflow-hidden relative min-w-0">
          {/* TOAST NOTIFICATION */}
          {gdriveStatusToast && (
            <div className={`absolute top-4 right-4 z-50 px-4 py-2.5 rounded-xl border shadow-2xl flex items-center gap-2 text-xs font-bold animate-bounce ${
              gdriveStatusToast.isError 
                ? 'bg-red-950/90 border-red-500 text-red-200' 
                : 'bg-emerald-950/90 border-emerald-500 text-emerald-200'
            }`}>
              {gdriveStatusToast.isError ? <AlertCircle className="w-4 h-4 text-red-400" /> : <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
              <span>{gdriveStatusToast.message}</span>
            </div>
          )}

          {/* MESSAGES FEED */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-4 sm:space-y-6 scrollbar-thin">
            {messages.length === 0 ? (
              <div className="max-w-2xl mx-auto my-auto py-4 sm:py-12 text-center space-y-3 sm:space-y-6">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto text-emerald-400 shadow-xl shadow-emerald-500/10">
                  <Sparkles className="w-6 h-6 sm:w-8 sm:h-8" />
                </div>
                <div>
                  <h2 className="text-base sm:text-lg font-bold text-white">
                    {isPl ? 'Terminal Asystenta Antigravity Core' : isUk ? 'Термінал ШІ-Асистента Antigravity Core' : 'Antigravity Core Assistant Terminal'}
                  </h2>
                  <p className="text-[11px] sm:text-xs text-slate-400 max-w-md mx-auto mt-1 sm:mt-2 leading-relaxed px-2">
                    {isPl 
                      ? 'Opisz zadanie, wgraj pliki (Word, Excel, PDF, kod). Asystent wygeneruje gotowe dokumenty lub kod.' 
                      : isUk 
                      ? 'Опишіть будь-яке завдання або прикріпіть файли (Word, Excel, PDF, код). Асистент згенерує готові матеріали.' 
                      : 'Describe your task, upload files (Word, Excel, PDF, code). The assistant will generate completed documents.'}
                  </p>
                </div>

                {/* PRESET CHIPS */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-2.5 max-w-lg mx-auto text-left px-1">
                  {presetPrompts.map((preset, idx) => (
                    <button
                      key={idx}
                      onClick={() => setInputText(preset.prompt)}
                      className="p-2 sm:p-3 bg-slate-900/80 hover:bg-slate-800/90 border border-slate-800 hover:border-emerald-500/40 rounded-xl text-xs transition-all cursor-pointer group text-slate-300 hover:text-white"
                    >
                      <span className="font-bold text-emerald-400 group-hover:text-emerald-300 block mb-0.5 text-[11px] sm:text-xs">
                        {preset.title}
                      </span>
                      <span className="text-[10px] sm:text-[11px] text-slate-400 line-clamp-1 sm:line-clamp-2">
                        {preset.prompt}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((msg, index) => (
                <div
                  key={msg.id || index}
                  className={`flex gap-3 max-w-4xl ${msg.role === 'user' ? 'ml-auto flex-row-reverse' : 'mr-auto'}`}
                >
                  {/* Avatar */}
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 text-white font-bold text-xs shadow-md ${
                    msg.role === 'user'
                      ? 'bg-blue-600'
                      : 'bg-gradient-to-tr from-emerald-600 to-teal-400'
                  }`}>
                    {msg.role === 'user' ? (currentUserEmail?.charAt(0).toUpperCase() || 'U') : <Bot className="w-4 h-4" />}
                  </div>

                  {/* Content Bubble */}
                  <div className={`space-y-3 max-w-[88%] sm:max-w-[80%] ${
                    msg.role === 'user' ? 'items-end text-right' : 'items-start text-left'
                  }`}>
                    <div className={`p-4 rounded-2xl text-xs leading-relaxed border ${
                      msg.role === 'user'
                        ? 'bg-blue-950/40 border-blue-600/40 text-blue-100 rounded-tr-none'
                        : 'bg-slate-900/90 border-slate-800 text-slate-200 rounded-tl-none shadow-lg'
                    }`}>
                      {/* Pre-formatted message text */}
                      <div className="whitespace-pre-wrap font-sans text-xs break-words leading-relaxed space-y-2">
                        {msg.content}
                      </div>

                      {/* Attached/Generated Files Cards */}
                      {msg.files && msg.files.length > 0 && (
                        <div className="mt-4 pt-3 border-t border-slate-800/80 space-y-2">
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider text-left">
                            {msg.role === 'assistant' 
                              ? (isPl ? '📦 Wygenerowane pliki gotowe do zapisu:' : isUk ? '📦 Створені файли, готові до збереження:' : '📦 Generated files ready to save:')
                              : (isPl ? '📎 Załączone pliki:' : isUk ? '📎 Долучені файли:' : '📎 Attached files:')}
                          </p>
                          <div className="grid grid-cols-1 gap-2 text-left">
                            {msg.files.map((file) => (
                              <div
                                key={file.id}
                                className="p-3 bg-slate-950 border border-slate-800 hover:border-slate-700 rounded-xl flex flex-wrap items-center justify-between gap-3 shadow-inner"
                              >
                                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                  {renderFileIcon(file)}
                                  <div className="min-w-0 flex-1">
                                    <p className="text-xs font-bold text-white truncate" title={file.originalName}>
                                      {file.originalName}
                                    </p>
                                    <p className="text-[10px] text-slate-400">
                                      {formatFileSize(file.size)}
                                      {file.gdriveSynced && (
                                        <span className="ml-2 text-emerald-400 font-semibold">
                                          ✓ Google Drive
                                        </span>
                                      )}
                                    </p>
                                  </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex items-center gap-2 shrink-0">
                                  {/* Download Button */}
                                  <a
                                    href={`/api/admin/assistant/download/${file.id}?pin=${encodeURIComponent(getStoredPin())}`}
                                    download={file.originalName}
                                    className="px-2.5 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-[11px] font-bold transition-all flex items-center gap-1.5 shadow-sm cursor-pointer"
                                    title={isPl ? 'Pobierz na komputer' : isUk ? 'Зберегти на комп\'ютер' : 'Download to computer'}
                                  >
                                    <Download className="w-3.5 h-3.5" />
                                    <span>{isPl ? 'Na komputer' : isUk ? 'На комп\'ютер' : 'Download'}</span>
                                  </a>

                                  {/* Google Drive Button */}
                                  <button
                                    onClick={() => handleSaveToGoogleDrive(file)}
                                    disabled={gdriveSyncingId === file.id}
                                    className={`px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-all flex items-center gap-1.5 shadow-sm cursor-pointer ${
                                      file.gdriveSynced
                                        ? 'bg-emerald-700/60 hover:bg-emerald-600 text-emerald-200 border border-emerald-500/40'
                                        : 'bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-white'
                                    }`}
                                    title={isPl ? 'Zapisz na dysku Google Drive' : isUk ? 'Синхронізувати з Google Drive' : 'Save to Google Drive'}
                                  >
                                    {gdriveSyncingId === file.id ? (
                                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                    ) : (
                                      <Cloud className="w-3.5 h-3.5" />
                                    )}
                                    <span>
                                      {gdriveSyncingId === file.id
                                        ? (isPl ? 'Zapisywanie...' : isUk ? 'Запис...' : 'Saving...')
                                        : file.gdriveSynced
                                        ? '✓ Google Drive'
                                        : (isPl ? 'Na Google Drive' : isUk ? 'На Google Drive' : 'Save to GDrive')}
                                    </span>
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <span className="text-[10px] text-slate-500 px-1 font-mono">
                      {msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                  </div>
                </div>
              ))
            )}

            {/* Loading / Generating State */}
            {isSending && (
              <div className="flex gap-3 mr-auto max-w-xl">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-emerald-600 to-teal-400 flex items-center justify-center shrink-0 text-white shadow-md">
                  <Bot className="w-4 h-4 animate-spin" />
                </div>
                <div className="p-3.5 bg-slate-900 border border-slate-800 rounded-2xl rounded-tl-none flex items-center gap-2.5 text-xs text-emerald-400 shadow-md">
                  <Sparkles className="w-4 h-4 animate-pulse" />
                  <span>
                    {isPl 
                      ? 'Antigravity przetwarza zapytanie i generuje odpowiedź...' 
                      : isUk 
                      ? 'Antigravity аналізує дані та готує результати...' 
                      : 'Antigravity is analyzing data and generating output...'}
                  </span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* BOTTOM INPUT & ATTACHMENT BAR */}
          <div className="p-2 sm:p-4 bg-slate-900 border-t border-slate-800 shrink-0 space-y-1.5 sm:space-y-2 sticky bottom-0 z-10">
            {/* STAGED FILES CHIPS */}
            {stagedFiles.length > 0 && (
              <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-thin">
                {stagedFiles.map(f => (
                  <div
                    key={f.id}
                    className="flex items-center gap-1.5 px-2 py-1 bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-200 shrink-0"
                  >
                    {renderFileIcon(f)}
                    <span className="max-w-[120px] sm:max-w-[150px] truncate text-[11px] font-bold">{f.originalName}</span>
                    <span className="text-[10px] text-slate-400">({formatFileSize(f.size)})</span>
                    <button
                      onClick={() => handleRemoveStagedFile(f.id)}
                      className="p-0.5 hover:bg-slate-700 rounded text-slate-400 hover:text-white cursor-pointer"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-end gap-1.5 sm:gap-2 bg-slate-950 border border-slate-800 rounded-xl p-1.5 sm:p-2 focus-within:border-emerald-500/60 transition-all">
              {/* File Attachment Button */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFilesSelected}
                multiple
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="p-2 sm:p-2.5 bg-slate-900 hover:bg-slate-800 text-slate-400 hover:text-emerald-400 rounded-lg transition-colors cursor-pointer disabled:opacity-50 shrink-0"
                title={isPl ? 'Wgraj dowolne pliki (Word, Excel, PDF, kod, obrazy)' : isUk ? 'Підвантажити будь-які файли (Word, Excel, PDF, код, фото)' : 'Upload any files'}
              >
                {isUploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
              </button>

              {/* Textarea Input - min-w-0 ensures flex child expands fully on mobile */}
              <textarea
                ref={textareaRef}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                placeholder={isPl 
                  ? 'Napisz polecenie (Shift+Enter - nowa linia)...' 
                  : isUk 
                  ? 'Введіть повідомлення для ШІ-асистента (Shift+Enter - новий рядок)...' 
                  : 'Type instructions for assistant (Shift+Enter for newline)...'}
                rows={1}
                className="flex-1 min-w-0 max-h-32 bg-transparent text-xs sm:text-sm text-slate-100 placeholder-slate-500 focus:outline-hidden resize-none py-1.5 sm:py-2 px-1 scrollbar-thin leading-relaxed"
              />

              {/* Send Button */}
              <button
                type="button"
                onClick={handleSendMessage}
                disabled={(!inputText.trim() && stagedFiles.length === 0) || isSending}
                className="p-2 sm:p-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed shadow-md shadow-emerald-600/20 shrink-0"
                title={isPl ? 'Wyślij polecenie' : isUk ? 'Надіслати завдання' : 'Send prompt'}
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            
            <div className="flex items-center justify-between text-[9px] sm:text-[10px] text-slate-500 px-1">
              <span className="truncate">{isPl ? 'Obsługa: Word, Excel, PDF, kod, zip' : isUk ? 'Файли: Word (.docx), Excel (.xlsx), PDF, код, фото, zip' : 'Files: Word (.docx), Excel (.xlsx), PDF, code, images'}</span>
              <span className="shrink-0 ml-2">{isPl ? 'Zapis: PC & Google Drive' : isUk ? 'Запис: ПК & Google Drive' : 'Save: PC & Google Drive'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* GOOGLE DRIVE TARGET FOLDER MODAL */}
      {showFolderModal && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-md w-full p-5 space-y-4 shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2 text-amber-400">
                <Cloud className="w-5 h-5" />
                <h3 className="text-sm font-bold text-white">
                  {isPl ? 'Katalog Google Drive' : isUk ? 'Папка призначення на Google Drive' : 'Google Drive Target Directory'}
                </h3>
              </div>
              <button onClick={() => setShowFolderModal(null)} className="text-slate-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-xs text-slate-300">
              {isPl 
                ? 'Wskaż katalog docelowy w chmurze rclone dla automatycznego zapisu plików:' 
                : isUk 
                ? 'Вкажіть цільовий каталог у хмарі rclone для автозбереження файлів:' 
                : 'Specify rclone remote folder for saving generated files:'}
            </p>

            <div className="space-y-2">
              <input
                type="text"
                value={gdriveTargetFolder}
                onChange={(e) => setGdriveTargetFolder(e.target.value)}
                placeholder="gdrive:DeepResearchHub_Admin/"
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white font-mono focus:border-amber-500 focus:outline-hidden"
              />

              <div className="flex flex-wrap gap-1.5 pt-1">
                {[
                  'gdrive:DeepResearchHub_Admin/',
                  'gdrive:НІС-ЕКО/',
                  'gdrive:ГІС-ЕКО_ПРО_5/',
                  'gdrive:Інфразвукові_Станції_ГЦСК/',
                  'gdrive:Автономна_Інфразвукова_Система_ППО/'
                ].map((folder) => (
                  <button
                    key={folder}
                    type="button"
                    onClick={() => setGdriveTargetFolder(folder)}
                    className="px-2 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded text-[10px] transition-colors border border-slate-700"
                  >
                    {folder.replace('gdrive:', '')}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setShowFolderModal(null)}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold transition-all cursor-pointer"
              >
                {isPl ? 'Zapisz ustawienie' : isUk ? 'Зберегти налаштування' : 'Save setting'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
